import { useEffect, useState } from 'react'
import { Card, Col, Empty, InputNumber, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'

import { maintenanceReportsApi } from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface TopMachine {
  machine_id: string
  cost: number
}

interface TrendRow {
  month: string
  from: string
  to: string
  total_maintenance_cost: number
  total_production_kg: number | null
  maintenance_cost_per_kg: number | null
  department_split: Record<string, number>
  top_machines: TopMachine[]
}

/**
 * Phase 3.1 — Monthly Cost-per-kg trend.
 *
 * One row per month for the last N months. Backend computes:
 *   cost/kg = total maintenance cost (month) / total production kg (month)
 *
 * When no production rows exist for a month, cost/kg is null (never 0/0).
 * The split columns show how the cost was distributed across departments
 * and top-5 machines — read-only, server-computed.
 */
export default function MaintenanceCostPerKgTrendPage() {
  const { plantId } = usePlantId()
  const { deptName, machineCode } = useMasterLookups()
  const [months, setMonths] = useState<number>(6)
  const [, setRefreshTick] = useState(0)

  // Plant changes don't have their own filter bar on this lean page — keep
  // the query keyed on plantId so it auto-refetches when the global plant
  // picker updates.
  useEffect(() => {
    if (plantId) setRefreshTick((n) => n + 1)
  }, [plantId])

  const { data, isLoading } = useQuery({
    queryKey: ['cost-per-kg-trend', plantId, months],
    enabled: !!plantId,
    queryFn: () => maintenanceReportsApi.costPerKgMonthlyTrend({ plant_id: plantId, months }),
  })
  const rows = (data ?? []) as unknown as TrendRow[]

  const totalCost = rows.reduce((a, r) => a + (r.total_maintenance_cost ?? 0), 0)
  const totalKg = rows.reduce((a, r) => a + (r.total_production_kg ?? 0), 0)
  const overallCpk = totalKg > 0 ? totalCost / totalKg : null

  const columns = [
    { title: 'Month', dataIndex: 'month', key: 'm', width: 110,
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Maintenance cost', dataIndex: 'total_maintenance_cost', key: 'c', width: 160,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Production (kg)', dataIndex: 'total_production_kg', key: 'kg', width: 140,
      render: (v: number | null) => v == null ? <Text type="secondary">—</Text> : v.toFixed(2),
    },
    {
      title: 'Cost / kg (₹)', dataIndex: 'maintenance_cost_per_kg', key: 'cpk', width: 160,
      render: (v: number | null) => v == null
        ? <Tag color="default">no production</Tag>
        : <Text strong>₹ {v.toFixed(4)}</Text>,
    },
    {
      title: 'Department split', dataIndex: 'department_split', key: 'ds',
      render: (v: Record<string, number>) => {
        const entries = Object.entries(v ?? {}).slice(0, 4)
        if (entries.length === 0) return <Text type="secondary">—</Text>
        return (
          <Space size={4} wrap>
            {entries.map(([id, cost]) => (
              <Tag key={id}>{deptName(id)}: ₹ {cost.toFixed(0)}</Tag>
            ))}
          </Space>
        )
      },
    },
    {
      title: 'Top machines', dataIndex: 'top_machines', key: 'tm',
      render: (v: TopMachine[]) => {
        if (!v || v.length === 0) return <Text type="secondary">—</Text>
        return (
          <Space size={4} wrap>
            {v.map((m) => (
              <Tag key={m.machine_id}>{machineCode(m.machine_id)}: ₹ {m.cost.toFixed(0)}</Tag>
            ))}
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Maintenance Cost / kg — Monthly Trend"
        subtitle="Backend-calculated cost-per-kg across the last N months."
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Text>Months:</Text>
          <InputNumber
            min={1} max={24} value={months}
            onChange={(v) => setMonths(Number(v) || 6)}
          />
          <Text type="secondary">
            Plant: <code>{plantId.slice(0, 8) || 'none'}</code>
          </Text>
        </Space>
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8}>
          <Card><Statistic title="Total cost (window)" value={totalCost} prefix="₹" precision={2} /></Card>
        </Col>
        <Col xs={12} md={8}>
          <Card><Statistic title="Total production (kg)" value={totalKg} precision={2} /></Card>
        </Col>
        <Col xs={12} md={8}>
          <Card>
            <Statistic
              title="Overall cost / kg"
              value={overallCpk ?? '—'}
              prefix={overallCpk == null ? '' : '₹'}
              precision={overallCpk == null ? 0 : 4}
            />
          </Card>
        </Col>
      </Row>

      {rows.length === 0 && !isLoading
        ? <Empty description="No trend data yet" />
        : <DataTable rowKey="month" columns={columns} dataSource={rows} loading={isLoading} />}
    </div>
  )
}
